import {
  JSX,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RevolutionSliderTypes } from '../enums';
import {
  HomeShortcut,
  SaveEveryDayCategoryItem,
  SmartSpendingItem,
} from '../models/Categories';
import { ProductListModel } from '../models/Product';
import { SlideshowItemModel } from '../models/RevolutionSlider';
import { categoryService } from '../services/category';
import { revolutionSliderService } from '../services/revolutionSlider';

type CarouselsContextValue = {
  smartSpendings?: SmartSpendingItem[];
  saveEveryDay?: SaveEveryDayCategoryItem[];
  slideshow?: SlideshowItemModel[];
  topDeals?: ProductListModel[];
  shortcuts?: HomeShortcut[];
  loading: {
    smartSpendings: boolean;
    saveEveryDay: boolean;
    slideshow: boolean;
    topDeals: boolean;
    shortcuts: boolean;
  };
  reloadHomeCategories: () => Promise<void>;
  reloadSlideshow: () => Promise<void>;
};

export const CarouselsContext = createContext({} as CarouselsContextValue);

export function CarouselsProvider(props: Readonly<{ children: JSX.Element }>) {
  const [smartSpendings, setSmartSpendings] = useState<SmartSpendingItem[]>();
  const [saveEveryDay, setSaveEveryDay] =
    useState<SaveEveryDayCategoryItem[]>();
  const [slideshow, setSlideshow] = useState<SlideshowItemModel[]>();
  const [topDeals, setTopDeals] = useState<ProductListModel[]>();
  const [shortcuts, setShortcuts] = useState<HomeShortcut[]>();

  const [homeCategoriesLoading, setHomeCategoriesLoading] = useState(true);
  const [slideshowLoading, setSlideshowLoading] = useState(true);
  const [shortcutsLoading, setShortcutsLoading] = useState(true);
  const mountedRef = useRef(true);

  const reloadHomeCategories = useCallback(async () => {
    setHomeCategoriesLoading(true);

    try {
      const result = await categoryService.getHomeCategories();

      if (!mountedRef.current) return;
      if (!result.hasErrors) {
        setSmartSpendings(result.data.smartSpendings);
        setSaveEveryDay(result.data.saveEveryday);
        setTopDeals(result.data.topDeals);
      }
    } finally {
      if (mountedRef.current) setHomeCategoriesLoading(false);
    }
  }, []);

  const reloadSlideshow = useCallback(async () => {
    setSlideshowLoading(true);

    try {
      const result = await revolutionSliderService.getSlideshowItems(
        RevolutionSliderTypes.HomeSlideshow,
      );

      if (!mountedRef.current) return;
      if (!result.hasErrors) setSlideshow(result.data);
    } finally {
      if (mountedRef.current) setSlideshowLoading(false);
    }
  }, []);

  const reloadShortcut = useCallback(async () => {
    setShortcutsLoading(true);

    try {
      const result = await categoryService.getHomepageShortcuts();

      if (!mountedRef.current) return;
      if (!result.hasErrors) setShortcuts(result.data);
    } finally {
      if (mountedRef.current) setShortcutsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    void reloadHomeCategories();
    void reloadSlideshow();
    void reloadShortcut();

    return () => {
      mountedRef.current = false;
    };
  }, [reloadHomeCategories, reloadSlideshow, reloadShortcut]);

  const value = useMemo<CarouselsContextValue>(
    () => ({
      smartSpendings,
      saveEveryDay,
      slideshow,
      topDeals,
      shortcuts,
      loading: {
        smartSpendings: homeCategoriesLoading,
        saveEveryDay: homeCategoriesLoading,
        slideshow: slideshowLoading,
        topDeals: homeCategoriesLoading,
        shortcuts: shortcutsLoading,
      },
      reloadHomeCategories,
      reloadSlideshow,
      reloadShortcut,
    }),
    [
      smartSpendings,
      saveEveryDay,
      slideshow,
      topDeals,
      shortcuts,
      slideshowLoading,
      shortcutsLoading,
      reloadHomeCategories,
      reloadSlideshow,
      reloadShortcut,
    ],
  );

  return (
    <CarouselsContext.Provider value={value}>
      {props.children}
    </CarouselsContext.Provider>
  );
}

export const useCarousels = () => useContext(CarouselsContext);
